import type { IApiResponse } from '@picsart/cloud-storage/types/core/types/api';
import type { ICloudStorageHeaders } from '@picsart/cloud-storage/types/core/types/api/headers';

import { getContext } from '@/context';
import { getAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import { ErrorMessages } from '@/consts/errors';

export const getCloudStorageHeaders = (): ICloudStorageHeaders => {
  return getContext().utils.api.getHeaders() as ICloudStorageHeaders;
};

interface CloudStorageErrorHandlingParams<T> {
  operation: () => Promise<IApiResponse<T>>;
  errorMessage: string;
}

export const withCloudStorageErrorHandling = async <T>({
  operation,
  errorMessage,
}: CloudStorageErrorHandlingParams<T>): Promise<IApiResponse<T> | null> => {
  try {
    const result = await operation();

    // Only an explicit `false` is an expired token — `updateFileMeta` omits the
    // flag entirely, and would otherwise be retried on every call.
    if (result.authorized === false) {
      await getContext().handlers.refreshToken();
      return await operation();
    }

    return result;
  } catch {
    getAppStore().showToast(errorMessage, ToastType.Error);
    return null;
  }
};

/**
 * For writes, where an error payload has to count as failure: the drive resolves
 * one like a success, so a caller that only checks for null reports a write that
 * never happened as done.
 */
export const withCloudStorageWriteHandling = async <T>(
  params: CloudStorageErrorHandlingParams<T>
): Promise<IApiResponse<T> | null> => {
  const result = await withCloudStorageErrorHandling(params);

  if (result?.status !== 'error') return result;

  getAppStore().showToast(
    result.reason?.includes('space_limit')
      ? ErrorMessages.driveStorageFull
      : params.errorMessage,
    ToastType.Error
  );

  return null;
};
